import * as React from 'react'
import handleViewport from 'react-in-viewport'

import {
  getSafeUrlParametersFromWindow,
  i18n,
  useLoginCheck,
  useSiteConfig,
  useSiteDefinition,
} from '@thg-commerce/enterprise-core'
import {
  EnhancedTrackingEvent,
  pushToEnhancedDataLayer,
} from '@thg-commerce/enterprise-metrics/src/data_layer'
import { ReviewSort } from '@thg-commerce/enterprise-network/src/generated/graphql'
import { useTheme } from '@thg-commerce/enterprise-pages/src/Product/theme'
import { getPrice } from '@thg-commerce/enterprise-product-options/src/utils/price'
import { styled } from '@thg-commerce/enterprise-theme'
import { Price, Separator } from '@thg-commerce/gravity-elements'

import { useReviewsPage } from '../../../context'
import {
  ProductRating,
  ProductReviewContent,
  ReviewSortByDropdown,
} from '../../'
import { ReviewPagination } from '../../ReviewPagination'
import { NoReviewsFound } from '../NoReviewsFound'
import { ProductReviewSchema } from '../ProductReviewSchema'
import { DisclaimerText } from '../styles'
import {
  generateAddReviewFormUrlWithReturnTo,
  getReviewFiltersFromUrl,
} from '../utils'

import {
  DisplayMobileOnlyContainer,
  PriceContainer,
  ProductRatingWrapper,
  ReviewSortByWrapper,
  StyledReviewTitle,
  StyledSeparator,
} from './styles'

export type PaginatedProductReviewsProps = {
  customerReviewsTitle: string
  pageNumber?: string
  sortType?: string
  onPageChangeCallback?: () => void
  reviewsSku?: number
  reviewFilters?: string
}

interface ReviewPageTrackingEvent extends EnhancedTrackingEvent {
  product_id: string
  product_name?: string
}

const MAX_REVIEW_PER_PAGE = 10

const REVIEWS_INITIALLY_RENDERED = 5

const ReviewContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const pushHistory = (
  sortType?: ReviewSort,
  pageNumber?: number,
  reviewFilters?: string,
) => {
  const url = new URL(window.location.href)
  const params = new URLSearchParams(url.search)
  url.hash = ''

  if (pageNumber) {
    pageNumber > 1
      ? (params.has('sortType') && url.searchParams.delete('sortType'),
        url.searchParams.set('pageNumber', pageNumber.toString()))
      : params.has('pageNumber') && url.searchParams.delete('pageNumber')
  }

  if (sortType) {
    sortType !== ReviewSort.NewestToOldest
      ? url.searchParams.set('sortType', sortType)
      : pageNumber && pageNumber > 1
      ? params.has('sortType') && url.searchParams.delete('sortType')
      : params.get('sortType') !== (ReviewSort.NewestToOldest && null) &&
        url.searchParams.set('sortType', sortType)
  }

  if (reviewFilters) {
    url.searchParams.set('reviewFilters', reviewFilters)
  }

  window.history.pushState({}, '', url.toString())
}

export const PaginatedProductReviews = (
  props: PaginatedProductReviewsProps,
) => {
  const { product, selectedVariant } = useReviewsPage()
  const { originUrl, defaultLocale, defaultCurrency } = useSiteDefinition()
  const { useExternalIdentifier } = useSiteConfig()
  const isLoggedIn = useLoginCheck()

  const theme = useTheme()

  const i18nText = {
    failedToFetch: i18n('review.content.failed.text'),
    customerReviewsDisclaimer: i18n('reviews.disclaimer.text'),
    label: i18n('product.review.sort.label'),
    rrpLabel: i18n('general.rrp.text'),
    saveLabel: i18n('general.save.text'),
    fromLabel: i18n('reviews.paginatedproductreviews.productblock.from.text'),
  }

  const reviewSortByOptions = [
    {
      sortByType: ReviewSort.NewestToOldest,
      text: i18n('product.review.sort.created'),
    },
    {
      sortByType: ReviewSort.HighestToLowestRating,
      text: i18n('product.review.sort.rating'),
    },
    {
      sortByType: ReviewSort.MostToLeastPositiveVotes,
      text: i18n('product.review.sort.votes'),
    },
  ]

  let existingParams:
    | PaginatedProductReviewsProps
    | { [key: string]: string } = {}

  existingParams = { ...props }

  if (typeof window !== 'undefined') {
    const params = getSafeUrlParametersFromWindow(window, '*')

    for (const [key, value] of Object.entries(params)) {
      existingParams[key] = value
    }
  }

  const pageNumber =
    existingParams.pageNumber && parseInt(existingParams.pageNumber, 10) >= 1
      ? parseInt(existingParams.pageNumber, 10)
      : undefined

  const sort = reviewSortByOptions.find(
    (sort) => sort.sortByType === existingParams.sortType,
  )?.sortByType

  const reviewFilters = existingParams.reviewFilters
    ? decodeURIComponent(existingParams.reviewFilters)
    : ''

  const formattedReviewFilters = getReviewFiltersFromUrl(reviewFilters)

  React.useEffect(() => {
    pushHistory(sort, pageNumber, reviewFilters)
    window.scrollTo(0, 0)
  }, [pageNumber, sort, reviewFilters])

  React.useEffect(() => {
    if (!product) {
      return
    }

    pushToEnhancedDataLayer<ReviewPageTrackingEvent>({
      event: 'review_see_all',
      product_id:
        useExternalIdentifier && product.externalIdentifier
          ? product.externalIdentifier
          : product.sku,
      product_name: product.title,
    })
  }, [useExternalIdentifier, product])

  if (!product?.reviews || product.reviews.total === 0) {
    return <NoReviewsFound />
  }

  const reviewContent = product?.reviews.reviews?.reviews?.map(
    (review, index) => {
      if (index < REVIEWS_INITIALLY_RENDERED) {
        return (
          <ProductReviewContent
            productTitle={product.title}
            customerReview={review}
            key={review.id}
            sku={product.sku}
          />
        )
      }

      const ReviewContentInViewport = handleViewport(ProductReviewContent)
      return (
        <ReviewContentInViewport
          productTitle={product.title}
          customerReview={review}
          key={review.id}
          sku={product.sku}
        />
      )
    },
  )

  const handleParamChange = (sortType, newPageNumber) => {
    const href = `${originUrl}${product.url.replace('.html', '.reviews')}`

    let query = ''

    if (newPageNumber > 1) {
      query = `pageNumber=${newPageNumber}`
    }

    if (sortType && sortType !== ReviewSort.NewestToOldest) {
      if (query) {
        query = `${query}&`
      }
      query = `${query}sortType=${sortType}`
    }

    if (formattedReviewFilters?.scores) {
      if (query) {
        query = `${query}&`
      }
      query = `${query}reviewFilters=${existingParams.reviewFilters}`
    }

    if (query) {
      query = `?${query}`
    }

    window.location.href = `${href}${query}`
  }

  return (
    <div data-testid="paginated-product-reviews">
      <ProductReviewSchema product={product} />
      <StyledReviewTitle
        textStyle={theme.pageTheme?.productReviewContent?.title?.textStyle}
      >
        {`${product.title} ${props.customerReviewsTitle}`}
      </StyledReviewTitle>
      <DisplayMobileOnlyContainer>
        <Separator />
        {selectedVariant?.price && (
          <PriceContainer>
            <Price
              priceTheme={{
                price: theme.pageTheme.productSummary.price.price,
                rrp: theme.pageTheme.productSummary.price.rrp,
                savings: theme.pageTheme.productSummary.price.savings,
                priceWithRRP: theme.pageTheme.productSummary.price.priceWithRRP,
              }}
              priceDirection={theme.pageTheme.productSummary.price.direction}
              displayRRPText={
                theme.pageTheme.productSummary.price.displayRRPText
              }
              pricing={getPrice(
                selectedVariant,
                defaultCurrency,
                defaultLocale,
              )}
              i18nText={{
                rrpLabel: i18nText.rrpLabel,
                saveLabel: i18nText.saveLabel,
                fromLabel: i18nText.fromLabel,
              }}
              hasFromPrice={
                !selectedVariant?.price.price?.displayValue &&
                product.hasFromPrice
              }
            />
          </PriceContainer>
        )}
        <StyledSeparator />
      </DisplayMobileOnlyContainer>
      <ProductRatingWrapper>
        <ProductRating
          starRating={product.reviews.averageScore}
          numberOfReviews={product.reviews.total}
          count1Score={product.reviews.count1Score}
          count2Score={product.reviews.count2Score}
          count3Score={product.reviews.count3Score}
          count4Score={product.reviews.count4Score}
          count5Score={product.reviews.count5Score}
          averageFeatureScores={product.reviews.featureScores}
          showInlineFeatureScores={false}
          createReviewLink={generateAddReviewFormUrlWithReturnTo(
            product.sku,
            originUrl,
            isLoggedIn,
          )}
          filters={{ scores: formattedReviewFilters.scores }}
          url={product.url.replace('.html', '.reviews')}
          enableFocusOutline={false}
        />
      </ProductRatingWrapper>
      <StyledSeparator />
      <ReviewSortByWrapper>
        <DisclaimerText>{i18nText.customerReviewsDisclaimer}</DisclaimerText>
        <StyledSeparator />
        <ReviewSortByDropdown
          defaultValue={sort}
          onChange={(sortType) => {
            handleParamChange(sortType, 1)
          }}
          sortByOptions={reviewSortByOptions}
          label={i18nText.label}
        />
      </ReviewSortByWrapper>
      <StyledSeparator />
      <ReviewContainer>{reviewContent}</ReviewContainer>
      <ReviewPagination
        reviewsPerPage={MAX_REVIEW_PER_PAGE}
        reviewsTotal={product.reviews.reviews?.total || 0}
        activePage={pageNumber || 1}
        onPageChange={(newPageNumber) => {
          handleParamChange(sort, newPageNumber)
        }}
        sortName={sort || ReviewSort.NewestToOldest}
      />
    </div>
  )
}
